import { useEffect, useState } from 'react';

import { Modal, useToast } from '../ui/Overlay';
import { Button } from '../ui/Button';
import { Field, Input, Select } from '../ui/Field';
import { OrgField, useOrgField } from './OrgField';
import { useMutation } from '../../hooks/useResource';
import { useAuth } from '../../auth/AuthProvider';
import { api } from '../../lib/api';

/**
 * Add a vehicle.
 *
 * Only the registration is required — everything else can be filled in later,
 * and demanding a VIN before someone can start tracking a van is the kind of
 * form that gets worked around with placeholder data.
 *
 * A manager must place the vehicle in one of their own fleets, because that is
 * the only place they have authority; the server enforces the same rule, this
 * just makes it obvious before they submit.
 */
export function CreateVehicleModal({ open, onClose, fleets, onCreated }) {
  const toast = useToast();
  const { scope } = useAuth();

  const org = useOrgField();
  const empty = {
    regNumber: '', fleetId: '', orgId: '', make: '', model: '', year: '', vin: '', vehicleType: '',
  };
  const [form, setForm] = useState(empty);

  const isFleetScoped = Boolean(scope?.fleets);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useMutation(async () => {
    await api.post('/api/vehicles', {
      regNumber: form.regNumber.trim().toUpperCase(),
      // Only a platform administrator sends this; for everyone else the server
      // fills it in from their own account.
      orgId: org.visible ? form.orgId : undefined,
      fleetId: form.fleetId || undefined,
      make: form.make || undefined,
      model: form.model || undefined,
      year: form.year || undefined,
      vin: form.vin || undefined,
      vehicleType: form.vehicleType || undefined,
    });
  });

  const submit = async () => {
    try {
      await create.run();
      toast.success(`${form.regNumber.toUpperCase()} added.`);
      setForm(empty);
      onCreated();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const missingFleet = isFleetScoped && !form.fleetId;
  const missingOrg = org.visible && !form.orgId;

  // A platform administrator sees every organisation's fleets, so once they
  // have chosen an organisation the fleet list has to narrow to it — offering
  // another tenant's depot would be a cross-organisation mistake waiting to
  // be made.
  const availableFleets = (fleets || []).filter(
    (fleet) => !org.visible || !form.orgId || fleet.org_id === form.orgId
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a vehicle"
      description="Fit a device to it afterwards, from the vehicle or the device page."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.pending}
            onClick={submit}
            disabled={!form.regNumber.trim() || missingFleet || missingOrg}
          >
            Add vehicle
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Registration number" required hint="Shown everywhere this vehicle appears.">
          <Input value={form.regNumber} onChange={set('regNumber')} placeholder="KA01AB1234" autoFocus />
        </Field>

        {org.visible && (
          <OrgField
            value={form.orgId}
            onChange={(value) => setForm((current) => ({ ...current, orgId: value, fleetId: '' }))}
            organizations={org.organizations}
            loading={org.loading}
          />
        )}

        <Field
          label="Fleet"
          required={isFleetScoped}
          hint={
            isFleetScoped
              ? 'You can add vehicles to the fleets you manage.'
              : 'Optional — a vehicle with no fleet is visible to administrators only.'
          }
        >
          <Select
            value={form.fleetId}
            onChange={set('fleetId')}
            placeholder={isFleetScoped ? 'Choose a fleet' : 'No fleet'}
            options={availableFleets.map((fleet) => ({ value: fleet.id, label: fleet.name }))}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Make">
            <Input value={form.make} onChange={set('make')} placeholder="Tata" />
          </Field>
          <Field label="Model">
            <Input value={form.model} onChange={set('model')} placeholder="Ace" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Year">
            <Input type="number" value={form.year} onChange={set('year')} placeholder="2024" />
          </Field>
          <Field label="Type">
            <Input value={form.vehicleType} onChange={set('vehicleType')} placeholder="Light truck" />
          </Field>
        </div>

        <Field label="VIN" hint="Optional. Useful when reconciling with a maintenance system.">
          <Input value={form.vin} onChange={set('vin')} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Edit a vehicle.
 *
 * Registration is not here on purpose: it is the identity every trip, alert and
 * audit entry already refers to, and letting someone retype it turns last
 * month's history into a record about a vehicle that no longer appears to
 * exist. A vehicle that was registered wrong is retired and re-added.
 *
 * Moving it between fleets is the operation that actually matters here, because
 * a fleet is what scopes a manager — this is how a van changes hands.
 */
export function EditVehicleModal({ open, onClose, vehicle, fleets, onSaved }) {
  const toast = useToast();
  const { scope } = useAuth();

  const [form, setForm] = useState({});

  useEffect(() => {
    setForm({
      fleetId: vehicle?.fleet_id || '',
      make: vehicle?.make || '',
      model: vehicle?.model || '',
      year: vehicle?.year || '',
      vin: vehicle?.vin || '',
      vehicleType: vehicle?.vehicle_type || '',
      status: vehicle?.status || 'ACTIVE',
    });
  }, [vehicle]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const save = useMutation(async () => {
    await api.patch(`/api/vehicles/${vehicle.id}`, {
      // An empty string means "leave it alone" to the server's COALESCE, which
      // is what we want — this form never blanks a field it did not touch.
      fleetId: form.fleetId || undefined,
      make: form.make || undefined,
      model: form.model || undefined,
      year: form.year || undefined,
      vin: form.vin || undefined,
      vehicleType: form.vehicleType || undefined,
      status: form.status || undefined,
    });
  });

  const submit = async () => {
    try {
      await save.run();
      toast.success(`${vehicle.reg_number} updated.`);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!vehicle) return null;

  // A manager can only move a vehicle into a fleet they hold — the server
  // refuses otherwise, so the list must not offer the rest.
  const movableFleets = (fleets || []).filter(
    (fleet) =>
      fleet.org_id === vehicle.org_id && (!scope?.fleets || scope.fleets.includes(fleet.id))
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${vehicle.reg_number}`}
      description="The registration cannot be changed — history already refers to it."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.pending} onClick={submit}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Fleet" hint="Moving a vehicle changes who can see it.">
          <Select
            value={form.fleetId}
            onChange={set('fleetId')}
            placeholder="No fleet"
            options={movableFleets.map((fleet) => ({ value: fleet.id, label: fleet.name }))}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Make">
            <Input value={form.make} onChange={set('make')} />
          </Field>
          <Field label="Model">
            <Input value={form.model} onChange={set('model')} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Year">
            <Input type="number" value={form.year} onChange={set('year')} />
          </Field>
          <Field label="Type">
            <Input value={form.vehicleType} onChange={set('vehicleType')} />
          </Field>
        </div>

        <Field label="VIN">
          <Input value={form.vin} onChange={set('vin')} />
        </Field>

        <Field
          label="Status"
          hint="A retired vehicle keeps its history but stops appearing in the fleet list."
        >
          <Select
            value={form.status}
            onChange={set('status')}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'SUSPENDED', label: 'Off the road' },
              { value: 'ARCHIVED', label: 'Retired' },
            ]}
          />
        </Field>
      </div>
    </Modal>
  );
}
